import { useSignIn, useSignUp } from "@clerk/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthScreen } from "@/components/auth/auth-screen";
import { ConfirmNote } from "@/components/auth/confirm-note";
import { InlineError } from "@/components/auth/inline-error";
import { LegalConsent } from "@/components/auth/legal-consent";
import { PasswordField } from "@/components/auth/password-field";
import { PrimaryButton } from "@/components/auth/primary-button";
import { StrengthGauge } from "@/components/auth/strength-gauge";
import { TextField } from "@/components/auth/text-field";
import { TextButton, TextLink } from "@/components/auth/text-link";
import { authPath, emailLooksComplete } from "@/lib/auth-helpers";
import { clerkFailureMessage } from "@/lib/clerk-errors";
import { clerkUsernameFromEmail } from "@/lib/clerk-username";
import { markCodeSent } from "@/lib/code-sent";
import { confirmState } from "@/lib/confirm-password";

const SignUpAccountPage = () => {
  const navigate = useNavigate();
  const { fetchStatus, signUp } = useSignUp();
  const { signIn } = useSignIn();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exists, setExists] = useState(false);
  const [pending, setPending] = useState(false);
  const tooShort = password.length < 8;

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setExists(false);
    setError(null);
    if (!firstName.trim()) {
      setError("Enter your first name.");
      return;
    }
    if (!lastName.trim()) {
      setError("Enter your last name.");
      return;
    }
    if (!emailLooksComplete(email)) {
      setError("Enter a valid email address.");
      return;
    }
    if (tooShort || confirm !== password) {
      return;
    }
    setPending(true);
    const { error: createError } = await signUp.password({
      emailAddress: email.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      password,
      username: clerkUsernameFromEmail(email.trim()),
    });
    if (createError) {
      if (createError.code === "form_identifier_exists") {
        setExists(true);
      } else {
        setError(clerkFailureMessage(createError));
      }
      setPending(false);
      return;
    }
    const { error: sendError } = await signUp.verifications.sendEmailCode();
    if (sendError) {
      setError(clerkFailureMessage(sendError));
      setPending(false);
      return;
    }
    markCodeSent("sign-up");
    navigate(authPath("/signup/verify"));
  };

  const signInInstead = async () => {
    const identifier = email.trim();
    await signIn.create({ identifier });
    if (signIn.status === "needs_first_factor") {
      navigate(authPath("/login/password", { email: identifier }));
      return;
    }
    navigate(authPath("/login", { email: identifier }));
  };

  return (
    <AuthScreen
      eyebrow="Step 1 of 3 · Account"
      subtitle="Let’s get to know each other."
      title="Create your account"
    >
      <form className="flex flex-col gap-7" onSubmit={onSubmit}>
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-4">
            <TextField
              autoComplete="given-name"
              id="first-name"
              label="First Name"
              onChange={(event) => setFirstName(event.target.value)}
              value={firstName}
            />
            <TextField
              autoComplete="family-name"
              id="last-name"
              label="Last Name"
              onChange={(event) => setLastName(event.target.value)}
              value={lastName}
            />
          </div>
          <TextField
            autoComplete="email"
            id="signup-email"
            label="Email"
            onChange={(event) => {
              setEmail(event.target.value);
              setExists(false);
              setError(null);
            }}
            placeholder="name@company.com"
            type="email"
            value={email}
          />
          {exists ? (
            <p className="m-0 text-error text-sm leading-[1.45]" role="alert">
              An account with this email already exists.{" "}
              <TextButton
                onClick={() => {
                  signInInstead().catch(() => undefined);
                }}
              >
                Sign in instead
              </TextButton>
            </p>
          ) : null}
          <div className="flex flex-col gap-2">
            <PasswordField
              autoComplete="new-password"
              error={
                submitted && tooShort ? "Use at least 8 characters." : null
              }
              hideMessage
              id="signup-password"
              label="Password"
              onChange={setPassword}
              onRevealedChange={setRevealed}
              revealed={revealed}
              value={password}
            />
            <StrengthGauge
              failedShort={submitted && tooShort}
              id="signup-password-hint"
              password={password}
            />
          </div>
          <div className="flex flex-col gap-2">
            <PasswordField
              autoComplete="new-password"
              error={
                confirmState(password, confirm, submitted) === "mismatch"
                  ? "Passwords don’t match."
                  : null
              }
              hideMessage
              id="signup-confirm"
              label="Confirm Password"
              onChange={setConfirm}
              onRevealedChange={setRevealed}
              revealed={revealed}
              value={confirm}
            />
            <ConfirmNote
              confirm={confirm}
              id="signup-confirm-status"
              password={password}
              submitted={submitted}
            />
          </div>
          {exists ? null : <InlineError message={error} />}
        </div>
        <div className="flex flex-col gap-3.5">
          <PrimaryButton
            busy={pending || fetchStatus === "fetching"}
            type="submit"
          >
            Create Account
          </PrimaryButton>
          <LegalConsent />
        </div>
      </form>
      <p className="m-0 flex flex-wrap gap-1.5 border-n-200 border-t pt-6 text-[15px] text-n-600">
        <span>Already have an account?</span>
        <TextLink to={authPath("/login")}>Sign in</TextLink>
      </p>
      <div id="clerk-captcha" />
    </AuthScreen>
  );
};

export default SignUpAccountPage;
